import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getImagePresetOptions } from './imagePresets';
import { fileToDataUrl, processImageFile } from './imageProcessor';
import { showImageOptimizationNotification } from './imageUploadNotification';

/**
 * @param {object} params
 * @param {'setting'|'pdfEmbed'|'emailEmbed'|'posterUpload'|'eventPosterUpload'|'avatarUpload'|'formImage'|'formStaticImage'|'equipmentCatalog'|'storeIcon'|'mobileTheme'|'mobileThemeLogo'} params.preset
 * @param {string|null} [params.imageType]
 * @param {boolean} [params.preservePng] Keep PNG uploads as PNG (overrides preset; enabled for thermal types in settings).
 * @param {object} [params.presetOverrides] Extra options merged into the preset (e.g. form image dimensions).
 * @param {boolean} [params.rejectOnFailure] When true, do not fall back to the raw file on processing errors.
 * @param {(dataUrl: string, report: object) => void} [params.onProcessed]
 * @param {(error: Error) => void} [params.onError]
 * @returns {{ handleFiles: (files: FileList|File[]) => Promise<Array<{ dataUrl: string, report: object }>> }}
 */
export function useSmartImageUpload({
    preset,
    imageType = null,
    preservePng,
    presetOverrides = {},
    rejectOnFailure = false,
    onProcessed,
    onError,
}) {
    const { t } = useTranslation();

    const handleFiles = useCallback(async (files) => {
        const list = Array.from(files || []).filter(Boolean);
        const results = [];

        for (const file of list) {
            try {
                const overrides = { ...presetOverrides };
                if (preservePng != null)
                    overrides.preservePng = preservePng;
                const options = getImagePresetOptions(preset, imageType, overrides);
                const { dataUrl, report } = await processImageFile(file, options);
                results.push({ dataUrl, report });
                onProcessed?.(dataUrl, report);
                showImageOptimizationNotification(t, report);
            }
            catch (err) {
                console.error('Smart image upload failed', err);
                if (rejectOnFailure) {
                    onError?.(err);
                    continue;
                }
                const dataUrl = await fileToDataUrl(file);
                const report = { modified: false };
                results.push({ dataUrl, report });
                onProcessed?.(dataUrl, report);
            }
        }

        return results;
    }, [preset, imageType, preservePng, presetOverrides, rejectOnFailure, onProcessed, onError, t]);

    return { handleFiles };
}
